import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Separator } from "../ui/separator";
import { toast } from "react-toastify";
import apiService from "../../services/api.service";
import logger from "../../utils/logger";

const EditOpponentModal = ({
  isOpen,
  onClose,
  opponentId,
  onOpponentUpdated,
}) => {
  // تم إزالة متغيرات القضايا لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة
  const [formData, setFormData] = useState({
    name: "",
    opponent_type: "مدعى عليه",
    phone: "",
    email: "",
    address: "",
    lawyer_name: "",
    lawyer_phone: "",
    lawyer_office: "",
    notes: "",
    status: "active",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // تم إزالة جلب القضايا لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة

  useEffect(() => {
    if (opponentId && isOpen) {
      fetchOpponentDetails();
    }
  }, [opponentId, isOpen]);

  const fetchOpponentDetails = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get(`/opponents/${opponentId}`);

      if (response.success) {
        setFormData({
          name: response.data.name || "",
          opponent_type: response.data.opponent_type || "مدعى عليه",
          phone: response.data.phone || "",
          email: response.data.email || "",
          address: response.data.address || "",
          lawyer_name: response.data.lawyer_name || "",
          lawyer_phone: response.data.lawyer_phone || "",
          lawyer_office: response.data.lawyer_office || "",
          notes: response.data.notes || "",
          status: response.data.status || "active",
        });
      } else {
        toast.error(response.message || "حدث خطأ أثناء جلب بيانات الخصم");
      }
    } catch (error) {
      logger.error("Error fetching opponent details:", error);
      toast.error(
        error.response?.data?.message || "حدث خطأ أثناء جلب بيانات الخصم"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // مسح الخطأ عند تعديل الحقل
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    // تحديث القيمة مباشرة
    setFormData((prev) => ({ ...prev, [name]: value }));

    // مسح الخطأ عند تعديل الحقل
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "اسم الخصم مطلوب";
    }

    // تم إزالة التحقق من القضية لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة
    // تم إزالة التحقق من القضية

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "البريد الإلكتروني غير صحيح";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // إعداد البيانات للإرسال، مع استبعاد الحقول الفارغة التي قد تسبب مشاكل في الخادم
      const {
        phone,
        email,
        address,
        lawyer_name,
        lawyer_phone,
        lawyer_office,
        notes,
        ...requiredFields
      } = formData;

      const dataToSend = {
        ...requiredFields,
      };

      // تم إزالة حقول القضية من الإرسال

      // إضافة الحقول الاختيارية فقط إذا كانت لها قيمة
      if (phone && phone.trim() !== "") dataToSend.phone = phone;
      if (email && email.trim() !== "") dataToSend.email = email;
      if (address && address.trim() !== "") dataToSend.address = address;
      // لا نرسل case_number إلى الخادم، فقط case_id
      if (lawyer_name && lawyer_name.trim() !== "")
        dataToSend.lawyer_name = lawyer_name;
      if (lawyer_phone && lawyer_phone.trim() !== "")
        dataToSend.lawyer_phone = lawyer_phone;
      if (lawyer_office && lawyer_office.trim() !== "")
        dataToSend.lawyer_office = lawyer_office;
      if (notes && notes.trim() !== "") dataToSend.notes = notes;

      logger.debug("Updating opponent data:", dataToSend);
      const response = await apiService.opponents.updateOpponent(opponentId, dataToSend);

      if (response.success) {
        toast.success("تم تحديث بيانات الخصم بنجاح");
        setErrors({});
        if (onOpponentUpdated) {
          onOpponentUpdated(response.data);
        }
        onClose();
      } else {
        toast.error(response.message || "حدث خطأ أثناء تحديث بيانات الخصم");
      }
    } catch (error) {
      logger.error("Error updating opponent:", error);
      toast.error(
        error.response?.data?.message || "حدث خطأ أثناء تحديث بيانات الخصم"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" dir="rtl">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>تعديل بيانات الخصم</CardTitle>
          <Button variant="ghost" onClick={onClose}>
            ×
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">اسم الخصم *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">رقم الهاتف</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? "border-red-500" : ""}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm">{errors.phone}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">البريد الإلكتروني</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm">{errors.email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">العنوان</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">الحالة</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      handleSelectChange("status", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الحالة" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">نشط</SelectItem>
                      <SelectItem value="settled">تم تسوية</SelectItem>
                      <SelectItem value="inactive">غير نشط</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="opponent_type">نوع الخصم</Label>
                  <Select
                    value={formData.opponent_type}
                    onValueChange={(value) =>
                      handleSelectChange("opponent_type", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر نوع الخصم" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="مدعى عليه">مدعى عليه</SelectItem>
                      <SelectItem value="مدعي">مدعي</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">معلومات المحامي/الوكيل</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lawyer_name">اسم المحامي/الوكيل</Label>
                    <Input
                      id="lawyer_name"
                      name="lawyer_name"
                      value={formData.lawyer_name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lawyer_phone">هاتف المحامي</Label>
                    <Input
                      id="lawyer_phone"
                      name="lawyer_phone"
                      value={formData.lawyer_phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lawyer_office">مكتب المحاماة</Label>
                  <Input
                    id="lawyer_office"
                    name="lawyer_office"
                    value={formData.lawyer_office}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-2">
                <Label htmlFor="notes">ملاحظات</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  إلغاء
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "جاري الحفظ..." : "حفظ التغييرات"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EditOpponentModal;
